import { loadStripe as originalLoadStripe } from '@stripe/stripe-js';

export class StripeService {
  constructor(loadStripe = originalLoadStripe) {
    this.loadStripe = loadStripe;
  }

  async setup(config) {
    const stripe = await this.loadStripe(config.publishableKey);

    const appearance = this.setStripeStyle(config.computedStyle);

    const elements = stripe.elements({
      mode: config.paymentMode,
      amount: config.selectedAmount,
      currency: config.currency,
      appearance,
      locale: config.locale,
    });

    const element = elements.create('payment', {
      layout: 'tabs',
      defaultValues: config.defaultValues,
      fields: { billingDetails: { email: 'never', name: 'never' } },
      paymentMethodOrder: config.paymentMethodOrder,
    });

    element.on('change', config.onChange);
    element.mount(config.rootElement);

    return { stripe, elements };
  }

  setStripeStyle(style) {
    const appearance = {
      theme: 'stripe',
      labels: 'floating',
      variables: {
        borderRadius: style.getPropertyValue('--cmpr-form-control-border-radius'),
        colorBackground: style.getPropertyValue('--cmpr-color-white'),
        colorDanger: style.getPropertyValue('--cmpr-color-error'),
        fontFamily: style.getPropertyValue('font-family'),
        fontSizeBase: style.getPropertyValue('font-size'),
        fontLineHeight: style.getPropertyValue('line-height'),
        gridColumnSpacing: style.getPropertyValue('--cmpr-space-60'),
        gridRowSpacing: style.getPropertyValue('--cmpr-space-60'),
        tabSpacing: style.getPropertyValue('--cmpr-space-30'),
        tabIconColor: style.getPropertyValue('--cmpr-color-secondary-80'),
        tabIconSelectedColor: style.getPropertyValue('--cmpr-color-secondary-80'),
      },
      rules: {
        '.Input': {
          borderColor: style.getPropertyValue('--cmpr-color-neutral-40'),
          color: style.getPropertyValue('--cmpr-color-neutral-70'),
          lineHeight: style.getPropertyValue('line-height'),
          paddingBottom: style.getPropertyValue('--cmpr-space-60'),
          paddingLeft: style.getPropertyValue('--cmpr-space-60'),
          paddingTop: style.getPropertyValue('--cmpr-space-50'),
        },
        '.Input:hover': {
          borderColor: style.getPropertyValue('--cmpr-color-neutral-60'),
        },
        '.Input:focus': {
          borderColor: style.getPropertyValue('--cmpr-color-secondary-80'),
          boxShadow: 'none',
          color: style.getPropertyValue('--cmpr-color-neutral-80'),
          outline: `${style.getPropertyValue('--cmpr-color-secondary-80')} 1px solid`,
        },
        '.Input::placeholder': {
          fontWeight: style.getPropertyValue('--cmpr-font-weight-10'),
          color: style.getPropertyValue('--cmpr-color-neutral-60'),
        },
        '.Label': {
          color: style.getPropertyValue('--cmpr-color-neutral-60'),
          fontWeight: style.getPropertyValue('--cmpr-font-weight-10'),
          transition: 'transform 0s, opacity 0s',
        },
        '.Label--resting': {
          color: style.getPropertyValue('--cmpr-color-neutral-60'),
          fontSize: style.getPropertyValue('font-size'),
        },
        '.Label--floating': {
          fontSize: '0px',
        },
        '.Input--invalid': {
          borderWidth: style.getPropertyValue('--cmpr-border-width-20'),
          boxShadow: 'none',
        },
        '.Input--invalid:hover': {
          borderColor: 'var(--colorDanger)',
        },
        '.Input--invalid:focus': {
          borderColor: 'var(--colorDanger)',
          outline: 'var(--colorDanger) 1px solid',
        },
        '.Tab': {
          backgroundColor: style.getPropertyValue('--cmpr-button-background-color-outline'),
          borderColor: style.getPropertyValue('--cmpr-button-border-color-outline'),
          borderRadius: style.getPropertyValue('--cmpr-button-border-radius-medium'),
          borderWidth: style.getPropertyValue('--cmpr-button-border-width-outline-medium'),
          color: style.getPropertyValue('--cmpr-color-neutral-70'),
          fontSize: style.getPropertyValue('font-size'),
        },
        '.Tab:hover': {
          backgroundColor: style.getPropertyValue('--cmpr-button-background-color-outline-hover'),
        },
        '.Tab:active': {
          borderColor: style.getPropertyValue('--cmpr-color-secondary-80'),
          boxShadow: 'none',
        },
        '.Tab--selected': {
          borderColor: style.getPropertyValue('--cmpr-color-secondary-80'),
          boxShadow: 'none',
          color: style.getPropertyValue('--cmpr-button-text-color-outline'),
        },
        '.Tab--selected:hover': {
          color: style.getPropertyValue('--cmpr-button-text-color-outline'),
        },
        '.Tab--selected:focus': {
          borderColor: style.getPropertyValue('--cmpr-color-secondary-80'),
          boxShadow: 'none',
        },
      },
    };
    return appearance;
  }
}
